import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Search,
  Target,
  DollarSign,
  Clock,
  CheckCircle,
  AlertCircle,
  Pause,
  TrendingUp,
  Users,
  MoreVertical,
  X
} from 'lucide-react';
import { format } from 'date-fns';
import { API_URL } from '../config/api';

interface OpportunitiesProps {
  token: string;
}

interface Opportunity {
  id: string;
  title: string;
  company: string;
  contactName: string;
  contactEmail: string;
  source: string;
  status: string;
  priority: number;
  estimatedValue: number;
  createdAt: string;
  urgency: string;
  industry: string;
}

const statusConfig: Record<string, { label: string; color: string; icon: any }> = {
  new: { label: 'New', color: 'bg-gray-100 text-gray-800', icon: Target },
  discovery_scheduled: { label: 'Discovery Scheduled', color: 'bg-blue-100 text-blue-800', icon: Clock },
  in_discovery: { label: 'In Discovery', color: 'bg-purple-100 text-purple-800', icon: Users },
  proposal_pending: { label: 'Proposal Pending', color: 'bg-yellow-100 text-yellow-800', icon: AlertCircle },
  proposal_sent: { label: 'Proposal Sent', color: 'bg-orange-100 text-orange-800', icon: TrendingUp },
  negotiating: { label: 'Negotiating', color: 'bg-indigo-100 text-indigo-800', icon: DollarSign },
  won: { label: 'Won', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  lost: { label: 'Lost', color: 'bg-red-100 text-red-800', icon: AlertCircle },
  paused: { label: 'Paused', color: 'bg-gray-100 text-gray-600', icon: Pause }
};

export default function Opportunities({ token }: OpportunitiesProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newOpportunity, setNewOpportunity] = useState({
    title: '',
    company: '',
    contactName: '',
    contactEmail: '',
    source: 'website',
    industry: 'technology',
    companySize: 'small',
    urgency: 'medium',
    estimatedValue: 10000
  });

  const queryClient = useQueryClient();

  const { data: opportunities = [], isLoading } = useQuery<Opportunity[]>({
    queryKey: ['opportunities', searchTerm, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter) params.append('status', statusFilter);

      const response = await fetch(`${API_URL}/api/opportunities?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        // Return empty array on error
        return [];
      }
      return response.json();
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof newOpportunity) => {
      const response = await fetch(`${API_URL}/api/opportunities`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to create opportunity');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opportunities'] });
      setShowCreateModal(false);
      setNewOpportunity({
        title: '',
        company: '',
        contactName: '',
        contactEmail: '',
        source: 'website',
        industry: 'technology',
        companySize: 'small',
        urgency: 'medium',
        estimatedValue: 10000
      });
    },
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusConfig = (status: string) => {
    return statusConfig[status] || statusConfig.new;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Opportunities</h1>
          <p className="text-gray-500">Manage your sales pipeline and opportunities</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn btn-primary flex items-center gap-2"
        >
          <Plus className="h-5 w-5" />
          New Opportunity
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search opportunities..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input pl-10"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input w-full sm:w-48"
          >
            <option value="">All Statuses</option>
            {Object.entries(statusConfig).map(([key, config]) => (
              <option key={key} value={key}>{config.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Opportunities table */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : opportunities.length === 0 ? (
        <div className="card text-center py-12">
          <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No opportunities found</h3>
          <p className="text-gray-500 mb-4">Get started by creating your first opportunity.</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn btn-primary inline-flex items-center gap-2"
          >
            <Plus className="h-5 w-5" />
            Create Opportunity
          </button>
        </div>
      ) : (
        <div className="card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="table-header">Opportunity</th>
                  <th className="table-header">Company</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">Priority</th>
                  <th className="table-header">Value</th>
                  <th className="table-header">Created</th>
                  <th className="table-header">Actions</th>
                </tr>
              </thead>
              <tbody>
                {opportunities.map((opp) => {
                  const statusInfo = getStatusConfig(opp.status);
                  const StatusIcon = statusInfo.icon;
                  return (
                    <tr key={opp.id} className="table-row border-b border-gray-100">
                      <td className="table-cell">
                        <div>
                          <p className="font-medium text-gray-900">{opp.title}</p>
                          <p className="text-gray-500">{opp.contactName}</p>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div>
                          <p className="text-gray-900">{opp.company}</p>
                          <p className="text-gray-500 text-xs">{opp.source}</p>
                        </div>
                      </td>
                      <td className="table-cell">
                        <span className={`badge ${statusInfo.color}`}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {statusInfo.label}
                        </span>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center">
                          <div className={`h-2 w-2 rounded-full mr-2 ${
                            opp.priority >= 8 ? 'bg-red-500' :
                            opp.priority >= 5 ? 'bg-yellow-500' : 'bg-green-500'
                          }`}></div>
                          <span>{opp.priority}/10</span>
                        </div>
                      </td>
                      <td className="table-cell font-medium">
                        {formatCurrency(opp.estimatedValue)}
                      </td>
                      <td className="table-cell text-gray-500">
                        {format(new Date(opp.createdAt), 'MMM d, yyyy')}
                      </td>
                      <td className="table-cell">
                        <button className="p-2 hover:bg-gray-100 rounded-lg">
                          <MoreVertical className="h-4 w-4 text-gray-400" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowCreateModal(false)}></div>
            <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full p-6 z-10">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">New Opportunity</h2>
                <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="h-6 w-6" />
                </button>
              </div>
              <form onSubmit={(e) => { e.preventDefault(); createMutation.mutate(newOpportunity); }}>
                <div className="space-y-4">
                  <div>
                    <label className="label">Title</label>
                    <input
                      type="text"
                      className="input"
                      value={newOpportunity.title}
                      onChange={(e) => setNewOpportunity({ ...newOpportunity, title: e.target.value })}
                      placeholder="e.g., CRM Integration Project"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="label">Company</label>
                      <input
                        type="text"
                        className="input"
                        value={newOpportunity.company}
                        onChange={(e) => setNewOpportunity({ ...newOpportunity, company: e.target.value })}
                        placeholder="Company name"
                        required
                      />
                    </div>
                    <div>
                      <label className="label">Industry</label>
                      <select
                        className="input"
                        value={newOpportunity.industry}
                        onChange={(e) => setNewOpportunity({ ...newOpportunity, industry: e.target.value })}
                      >
                        <option value="technology">Technology</option>
                        <option value="healthcare">Healthcare</option>
                        <option value="finance">Finance</option>
                        <option value="retail">Retail</option>
                        <option value="manufacturing">Manufacturing</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="label">Contact Name</label>
                      <input
                        type="text"
                        className="input"
                        value={newOpportunity.contactName}
                        onChange={(e) => setNewOpportunity({ ...newOpportunity, contactName: e.target.value })}
                        placeholder="Full name"
                        required
                      />
                    </div>
                    <div>
                      <label className="label">Contact Email</label>
                      <input
                        type="email"
                        className="input"
                        value={newOpportunity.contactEmail}
                        onChange={(e) => setNewOpportunity({ ...newOpportunity, contactEmail: e.target.value })}
                        placeholder="email@company.com"
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="label">Source</label>
                      <select
                        className="input"
                        value={newOpportunity.source}
                        onChange={(e) => setNewOpportunity({ ...newOpportunity, source: e.target.value })}
                      >
                        <option value="website">Website</option>
                        <option value="referral">Referral</option>
                        <option value="linkedin">LinkedIn</option>
                        <option value="email">Email</option>
                        <option value="voice_call">Voice Call</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="label">Estimated Value</label>
                      <input
                        type="number"
                        className="input"
                        value={newOpportunity.estimatedValue}
                        onChange={(e) => setNewOpportunity({ ...newOpportunity, estimatedValue: parseInt(e.target.value) })}
                        min="0"
                        step="1000"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="label">Urgency</label>
                      <select
                        className="input"
                        value={newOpportunity.urgency}
                        onChange={(e) => setNewOpportunity({ ...newOpportunity, urgency: e.target.value })}
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="critical">Critical</option>
                      </select>
                    </div>
                    <div>
                      <label className="label">Company Size</label>
                      <select
                        className="input"
                        value={newOpportunity.companySize}
                        onChange={(e) => setNewOpportunity({ ...newOpportunity, companySize: e.target.value })}
                      >
                        <option value="startup">Startup</option>
                        <option value="small">Small</option>
                        <option value="medium">Medium</option>
                        <option value="large">Large</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="mt-6 flex justify-end gap-3">
                  <button type="button" onClick={() => setShowCreateModal(false)} className="btn btn-secondary">
                    Cancel
                  </button>
                  <button type="submit" disabled={createMutation.isPending} className="btn btn-primary">
                    {createMutation.isPending ? 'Creating...' : 'Create Opportunity'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}